<template>
  <div ref="productList" class="cn-products-catalog">
    <div v-if="isCatalogFiltersVisible" class="cn-products-catalog__left">
      <div class="cn-products-catalog__categories">
        <template v-if="categories?.length">
          <div class="cn-products-catalog__category-title">
            {{ $t('category', 2) }}
          </div>
          <div class="cn-products-catalog__categories-list">
            <UiTreeview
              v-model="filter"
              :items="categories"
              :disabled="loading"
            />
          </div>
        </template>

        <template v-if="features?.length">
          <div class="cn-products-catalog__category-title">
            {{ $t('filter_features') }}
          </div>
          <div class="cn-products-catalog__categories-list">
            <UiTreeview
              v-model="featuresFilters"
              :items="features"
              :disabled="loading"
              disable-branch-nodes
            />
          </div>
        </template>
      </div>
    </div>
    <div class="cn-products-catalog__right">
      <div class="cn-products-catalog__filters">
        <div
          v-if="isCatalogFiltersVisible"
          class="cn-products-catalog__filters-toggler"
        >
          <UiButton
            variant="red"
            icon="filter"
            @click="toggleFilterDrawerState"
          >
            {{ $t('filter') }}
            <span v-if="activeFiltersLength">
              &nbsp;({{ activeFiltersLength }})</span
            >
          </UiButton>
          <UiDrawer v-model="filterDrawerState" :title="$t('filter')">
            <template #default>
              <UiTreeview
                v-if="categories?.length"
                v-model="filter"
                :items="categories"
                :disabled="loading"
              />
              <UiTreeview
                v-if="features?.length"
                v-model="featuresFilters"
                :items="features"
                :disabled="loading"
                disable-branch-nodes
              />
            </template>

            <template #footer>
              <UiButton variant="red" block @click="applyFilterProducts"
                >{{ $t('show_results') }}
              </UiButton>
            </template>
          </UiDrawer>
        </div>
        <div
          v-if="isCatalogFiltersVisible"
          class="cn-products-catalog__filters-list"
        >
          <div class="cn-products-catalog__filters-group">
            <UiTreeviewFilter
              v-model="filter"
              :items="categories"
              :disabled="loading"
            />
            <UiTreeviewFilter
              v-model="featuresFilters"
              :items="features"
              :disabled="loading"
            />
          </div>

          <button
            v-if="activeFiltersLength"
            class="cn-products-catalog__clear-filter-button"
            @click="clearFilter"
          >
            {{ $t('clear_filter') }}
          </button>
        </div>
      </div>
      <div class="cn-products-catalog__pagination">
        <ProductsList
          :per-row="productsPerRow"
          :products="products"
          :loading="loading"
          :total-pages-count="totalPagesCount"
          :current-page="currentPage"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import ProductsList from '@/components/ProductsList';
import UiTreeviewFilter from '@/components/ui/UiTreeviewFilter';
import UiTreeview from '@/components/ui/UiTreeview';
import UiDrawer from '@/components/ui/UiDrawer';
import UiButton from '@/components/ui/UiButton';

interface Props {
  categories?: any[];
  features?: any[];
  products?: any[];
  totalPagesCount?: number;
  loading?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'fetch', data: any): void;
}>();
const route = useRoute();
const productList = ref<any>(null);

const filter = ref<any[]>([]);
const filterRoute = computed(() => route.query.categories as string);
if (filterRoute.value)
  filter.value.push(
    ...filterRoute.value
      .slice()
      .split(',')
      .map((i) => Number(i)),
  );

const featuresFilters = ref<any[]>([]);
const featuresRoute = computed(() => route.query.features as string);
if (featuresRoute.value)
  featuresFilters.value.push(
    ...featuresRoute.value
      .slice()
      .split(',')
      .map((i) => Number(i)),
  );

const activeFiltersLength = computed(
  () => filter.value.length + featuresFilters.value.length,
);

const isCatalogFiltersVisible = computed(
  () => props.categories?.length || props.features?.length,
);

const clearFilter = () => {
  filter.value = [];
  featuresFilters.value = [];
};

const filterDrawerState = ref(false);
const toggleFilterDrawerState = () => {
  filterDrawerState.value = !filterDrawerState.value;
};

const productsPerRow = computed(() => {
  return props.categories?.length ? 3 : 4;
});

const currentPage = computed(() => route.query.page);

const filterProducts = (
  categories: any[],
  features: any[],
  page?: string | (string | null)[],
) => {
  emit('fetch', { categories, features, page });
  productList.value?.scrollIntoView({
    alignToTop: true,
    behavior: 'smooth',
  });
};

const applyFilterProducts = () => {
  toggleFilterDrawerState();
};

watch(currentPage, (newValue) => {
  filterProducts(filter.value, featuresFilters.value, newValue);
});

watch(
  [filter, featuresFilters],
  ([newFilter, newFeaturesFilters], [oldFilter, oldFeaturesFilters]) => {
    if (
      JSON.stringify(newFilter) === JSON.stringify(oldFilter) &&
      JSON.stringify(newFeaturesFilters) === JSON.stringify(oldFeaturesFilters)
    )
      return;

    filterProducts(newFilter, newFeaturesFilters);
  },
);
</script>
<style lang="scss">
.cn-products-catalog {
  --ab-canyon-products-catalog-category-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-products-catalog-clear-filter-button-color-text: var(
    --ab-canyon-color-dark
  );

  padding-top: var(--size-3xl);
  padding-bottom: var(--size-3xl);
  scroll-margin-top: calc(6 * var(--size-base));
  display: flex;
  margin-left: calc(var(--size-xs) * -1);
  margin-right: calc(var(--size-xs) * -1);
  &__left {
    padding-left: var(--size-xs);
    padding-right: var(--size-xs);
    width: calc(24 * var(--size-base));
    display: none;
    @media (min-width: 992px) {
      display: block;
    }
  }
  &__category-title {
    color: var(--ab-canyon-products-catalog-category-color-text);
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: var(--size-base);
  }
  &__right {
    padding-left: var(--size-xs);
    padding-right: var(--size-xs);
    width: 100%;
  }
  &__filters {
    flex-wrap: wrap;
    display: flex;
    margin-bottom: var(--size-base);
    @media (min-width: 992px) {
      flex-wrap: nowrap;
    }
  }
  &__filters-toggler {
    @media (min-width: 992px) {
      display: none;
    }
  }
  &__filters-list {
    align-items: flex-start;
    width: 100%;
    display: flex;
    margin-top: 1rem;
    order: 9999;
    @media (min-width: 656px) {
      min-height: 32px;
    }
    @media (min-width: 992px) {
      margin-top: 0px;
      order: 0;
    }
  }
  &__filters-group {
    display: flex;
    flex-direction: column;
    gap: var(--size-base);
  }
  &__clear-filter-button {
    color: var(--ab-canyon-products-catalog-clear-filter-button-color-text);

    font-size: 0.875rem;
    line-height: 1.25rem;
    padding: 0.375rem;
    flex-shrink: 0;
    display: none;
    margin-left: calc(var(--size-base) / 4);
    align-self: center;
    @media (min-width: 992px) {
      display: block;
    }
  }
  &__pagination {
    position: relative;
  }
}
</style>
